import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { List } from 'foundations-components/transitional/components/List';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer'; // Keep the import

import { SearchResultViewTracker } from 'marketplace-ui-tracking/events/marketplace';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useTrackAutocompleteSearchResultEvent } from '../../hooks/useTrackAutocompleteSearchResultEvent';
import { LISTBOX_ID } from '../constants';
import { getOptionId } from './SearchBar';
import { SearchOption } from './SearchOption';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_DROPDOWN_HEIGHT = '300px';
const StyledListItem = styled.li.withConfig({
  displayName: "SearchDropdown__StyledListItem",
  componentId: "sc-1mwqtba-0"
})(({
  theme: _theme,
  isActive
}) => {
  const theme = _theme;
  return {
    padding: theme.space['250'],
    backgroundColor: isActive ? theme.color['fill-surface-recessed'] : undefined,
    cursor: 'pointer'
  };
});
export function SearchDropdown({
  activeIndex,
  error,
  onResultSelect,
  options,
  searchTerm,
  setActiveIndex
}) {
  const [keyboardActive, setKeyboardActive] = useState(false);
  useEffect(() => {
    const handleKeyDown = e => {
      if (['ArrowUp', 'ArrowDown', 'Tab'].includes(e.key)) setKeyboardActive(true);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  return /*#__PURE__*/_jsx("div", {
    onMouseMove: () => setKeyboardActive(false),
    children: /*#__PURE__*/_jsx(CardWrapper, {
      borderRadius: "200",
      insetInlineEnd: '0',
      insetInlineStart: '0',
      marginBlockStart: "100",
      maxBlockSize: MAX_DROPDOWN_HEIGHT,
      overflow: "hidden",
      position: "absolute",
      zIndex: "300",
      children: /*#__PURE__*/_jsxs(ScrollContainer, {
        maxHeight: parseInt(MAX_DROPDOWN_HEIGHT, 10),
        minBlockSize: "50px",
        children: [error && /*#__PURE__*/_jsx(ErrorMessage, {
          type: "badRequest"
        }), !error && /*#__PURE__*/_jsx(List, {
          "data-test-id": "search-results-list",
          role: "listbox",
          id: LISTBOX_ID,
          children: options.map((item, index) => /*#__PURE__*/_jsx(SearchDropdownItem, {
            activeIndex: activeIndex,
            item: item,
            keyboardActive: keyboardActive,
            onResultSelect: onResultSelect,
            onSetActiveIndex: setActiveIndex,
            resultCount: options.length,
            resultIndex: index,
            searchTerm: searchTerm
          }, `${searchTerm}-${index}`))
        })]
      })
    })
  });
}
function SearchDropdownItem({
  activeIndex,
  item,
  keyboardActive,
  onResultSelect,
  onSetActiveIndex,
  resultCount,
  resultIndex,
  searchTerm
}) {
  const {
    getAutocompleteSearchResultEventProps
  } = useTrackAutocompleteSearchResultEvent({
    resultCount,
    searchTerm
  });
  const searchResultEventProps = useMemo(() => getAutocompleteSearchResultEventProps(item, resultIndex), [getAutocompleteSearchResultEventProps, item, resultIndex]);
  return /*#__PURE__*/_jsx(SearchResultViewTracker, Object.assign({}, searchResultEventProps, {
    children: /*#__PURE__*/_jsx(StyledListItem, {
      "aria-selected": activeIndex === resultIndex,
      id: getOptionId(resultIndex),
      isActive: activeIndex === resultIndex,
      onMouseMove: () => {
        // Only set the active index if the keyboard is not active.
        // Otherwise, when using the keyboard, the mouse will still hijack
        // the active index whenever the scroll position changes.
        if (!keyboardActive) onSetActiveIndex(resultIndex);
      },
      onClick: () => {
        onResultSelect(item, resultIndex);
      },
      role: "option",
      tabIndex: -1,
      children: /*#__PURE__*/_jsx(SearchOption, {
        item: item,
        searchTerm: searchTerm
      })
    })
  }));
}